import { ReactNode, useState, useEffect, useMemo, useCallback, useTransition } from 'react';
import { Page } from 'referral-types';
import useSWR from 'swr';
import { pageApi } from '../../api/service';
import { baseEndpoint as cacheKey } from '../../api/PageApi';
import { useParams } from 'react-router-dom';
import { PageContext } from './PageContext';
import { useAuth } from '../auth/useAuth';

export const PageProvider = ({ children }: { children: ReactNode }) => {
  const { pageId, pageName } = useParams<{ pageId: string; pageName: string }>();
  const { data: page, mutate } = useSWR<Page>(
    [cacheKey, pageName != null ? pageName : pageId],
    () => {
      return pageName != null ? pageApi.getPageByName(pageName) : pageApi.getPage(pageId!);
    },
    { suspense: true }
  );

  const { currentUser } = useAuth();
  const [isPageEditing, setIsPageEditing] = useState<boolean>();
  const [isPagePreviewing, setIsPagePreviewing] = useState<boolean>();
  const [isPublishingPage, setIsPublishingPage] = useState<boolean>();
  const [isUserEditing, setIsUserEditing] = useState<boolean>();
  const [isEditModeTransitionPending, startEditModeTransition] = useTransition();
  const [isPreviewModeTransitionPending, startPreviewModeTransition] = useTransition();

  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState<boolean>();
  const [hasUnpublishedPageChanges, setHasUnpublishedPageChanges] = useState<boolean>();
  const [hasUnpublishedGroupListChanges, setHasUnpublishedGroupListChanges] = useState<boolean>();
  const [hasUnpublishedGroupChanges, setHasUnpublishedGroupChanges] = useState<boolean>();
  const [hasUnpublishedReferralChanges, setHasUnpublishedReferralChanges] = useState<boolean>();

  const [isSavingChanges, setIsSavingChanges] = useState<boolean>();
  const [isSavingPageChanges, setIsSavingPageChanges] = useState<boolean>();
  const [isSavingGroupListChanges, setIsSavingGroupListChanges] = useState<boolean>();
  const [isSavingGroupChanges, setIsSavingGroupChanges] = useState<boolean>();
  const [isSavingReferralChanges, setIsSavingReferralChanges] = useState<boolean>();

  const [groupInsertionPosition, setGroupInsertionPosition] = useState<number | null>(null);

  // turn off edit mode if not allowed anymore
  useEffect(() => {
    if (
      (isPageEditing && !page?.canEdit) ||
      !currentUser ||
      (page?.userId !== currentUser.id && !currentUser.roles.includes('Admin'))
    ) {
      setIsPageEditing(false);
    }

    if (!isPageEditing && isPagePreviewing) {
      console.log(2);
      setIsPagePreviewing(false);
    }
  }, [isPageEditing, isPagePreviewing, currentUser, pageName, page?.canEdit, page?.userId]);

  // unselect inserter if not in editmode
  useEffect(() => {
    if (!isPageEditing) {
      if (groupInsertionPosition !== null) {
        setGroupInsertionPosition(null);
      }
    }
  }, [isPageEditing, groupInsertionPosition]);

  // set if app isSaving changes or not
  useEffect(() => {
    const isSaving =
      isSavingPageChanges || isSavingGroupListChanges || isSavingGroupChanges || isSavingReferralChanges || false;

    if (isSavingChanges !== isSaving) {
      setIsSavingChanges(isSaving);
    }
  }, [isSavingChanges, isSavingGroupChanges, isSavingGroupListChanges, isSavingReferralChanges, isSavingPageChanges]);

  // set if app has unpublished changes or not
  useEffect(() => {
    const isUnpublished =
      hasUnpublishedPageChanges ||
      hasUnpublishedGroupListChanges ||
      hasUnpublishedGroupChanges ||
      hasUnpublishedReferralChanges ||
      false;

    if (hasUnpublishedChanges !== isUnpublished) {
      setHasUnpublishedChanges(isUnpublished);
    }
  }, [
    hasUnpublishedChanges,
    hasUnpublishedGroupListChanges,
    hasUnpublishedPageChanges,
    hasUnpublishedGroupChanges,
    hasUnpublishedReferralChanges,
  ]);

  // todo
  // set if is page saving or not
  // useEffect(() => {
  //   const isSaving = isUpdatingPage;

  //   if (isSavingPageChanges  !== isSaving) {
  //     setIsSavingPageChanges(isSaving);
  //   }

  // }, [isSavingPageChanges, isUpdatingPage]);

  // set if page has unpublished changes or not
  useEffect(() => {
    const hasChanges = page?.revisionId !== page?.draftRevisionId;

    if (hasUnpublishedPageChanges !== hasChanges) {
      setHasUnpublishedPageChanges(hasChanges);
    }
  }, [hasUnpublishedPageChanges, page, page?.draftRevisionId, page?.revisionId]);

  // Memoize refresh function
  const refresh = useCallback(() => {
    mutate(undefined, { revalidate: true });
  }, [mutate]);

  // Toggle edit mode with transition for better perceived performance
  const toggleEditMode = useCallback(() => {
    startEditModeTransition(() => {
      console.log(3);
      setIsPageEditing((prev) => !prev);
    });
  }, []);

  // Toggle preview mode with transition for better perceived performance
  const togglePreviewMode = useCallback(() => {
    startPreviewModeTransition(() => {
      setIsPagePreviewing((prev) => !prev);
    });
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      page,
      mutator: mutate,
      refresh,

      isPageEditing,
      setIsPageEditing,
      toggleEditMode,
      isEditModeTransitionPending,
      isPagePreviewing,
      setIsPagePreviewing,
      togglePreviewMode,
      isPreviewModeTransitionPending,

      isPublishingPage,
      setIsPublishingPage,

      isUserEditing,
      setIsUserEditing,

      hasUnpublishedChanges,
      hasUnpublishedPageChanges,
      hasUnpublishedGroupListChanges,
      hasUnpublishedGroupChanges,
      hasUnpublishedReferralChanges,
      setHasUnpublishedChanges,
      setHasUnpublishedPageChanges,
      setHasUnpublishedGroupListChanges,
      setHasUnpublishedGroupChanges,
      setHasUnpublishedReferralChanges,

      isSavingChanges,
      isSavingPageChanges,
      isSavingGroupListChanges,
      isSavingGroupChanges,
      isSavingReferralChanges,
      setIsSavingChanges,
      setIsSavingPageChanges,
      setIsSavingGroupListChanges,
      setIsSavingGroupChanges,
      setIsSavingReferralChanges,

      groupInsertionPosition,
      setGroupInsertionPosition,
    }),
    [
      page,
      mutate,
      refresh,
      isPageEditing,
      setIsPageEditing,
      toggleEditMode,
      isEditModeTransitionPending,
      isPagePreviewing,
      setIsPagePreviewing,
      togglePreviewMode,
      isPreviewModeTransitionPending,
      isPublishingPage,
      setIsPublishingPage,
      isUserEditing,
      setIsUserEditing,
      hasUnpublishedChanges,
      hasUnpublishedPageChanges,
      hasUnpublishedGroupListChanges,
      hasUnpublishedGroupChanges,
      hasUnpublishedReferralChanges,
      setHasUnpublishedChanges,
      setHasUnpublishedPageChanges,
      setHasUnpublishedGroupListChanges,
      setHasUnpublishedGroupChanges,
      setHasUnpublishedReferralChanges,
      isSavingChanges,
      isSavingPageChanges,
      isSavingGroupListChanges,
      isSavingGroupChanges,
      isSavingReferralChanges,
      setIsSavingChanges,
      setIsSavingPageChanges,
      setIsSavingGroupListChanges,
      setIsSavingGroupChanges,
      setIsSavingReferralChanges,
      groupInsertionPosition,
      setGroupInsertionPosition,
    ]
  );

  return <PageContext.Provider value={contextValue}>{children}</PageContext.Provider>;
};
